// components/portal/SourceResumeChip.tsx
//
// "Resume campaign" above the composer, while the shortlist on screen has
// stopped with brands never analysed (stopped by hand, or cut by its credit budget). One click
// re-runs exactly those brands through the same path as RERUN_BRAND_ANALYSIS; brands already
// scored are not billed again. Self-gating on the store: the composer is
// shared by every module and only an open, stopped Source shortlist has anything to resume.
import React, { useCallback, useState } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { readCampaignRow } from '#lib/leasing/campaignRow';
import { ACTIVE_SOURCING_CAMPAIGN_STATUSES } from '#lib/leasing/sourcingCampaignLifecycle';
import { PILL_CLASS, PILL_STYLE } from '../slides/SlidesReviewLauncher';

// Lead eligibility (AGENTS.md): only fully scored rows count as evaluated.
const UNANALYSED_LEAD_STATUSES = ['PENDING', 'RUNNING', 'ABORTED', 'REVERTED'];

export const SourceResumeChip: React.FC = () => {
  const { kernel } = useKernel();
  const lang: 'en' | 'fr' = useKernelStore((state) => (state.language === 'fr' ? 'fr' : 'en'));
  const campaignId = useKernelStore((state) => state.sourceSelectedCampaignId ?? '');
  // One string, so an unrelated lead update does not re-render the composer.
  const pendingKey = useKernelStore((state) => {
    if (!campaignId) return '';
    const campaign = readCampaignRow(state.inventory, campaignId);
    // A live run finishes itself; a stopped one resumes here. A research run resumes its research
    // on the worker first (brands already researched are skipped), then scores.
    const status = String(campaign?.status ?? '').toUpperCase();
    if (!campaign || (ACTIVE_SOURCING_CAMPAIGN_STATUSES as readonly string[]).includes(status)) return '';
    const leads = state.inventory.Lead.registry as Record<string, { lastCampaignId?: string | null; brandId?: string; status?: string } | undefined>;
    const brandIds = new Set<string>();
    for (const lead of Object.values(leads)) {
      if (lead?.lastCampaignId === campaignId && lead.brandId && UNANALYSED_LEAD_STATUSES.includes(String(lead.status ?? '').toUpperCase())) {
        brandIds.add(lead.brandId);
      }
    }
    return [...brandIds].sort().join(',');
  });
  const [resuming, setResuming] = useState(false);

  const resume = useCallback(async () => {
    const k = kernel.current;
    if (!k || !campaignId || !pendingKey || resuming) return;
    setResuming(true);
    try {
      await k.rerunSourcingCampaignBrand(campaignId, pendingKey.split(','));
    } catch (error) {
      k.handleError(error instanceof Error ? error.message : 'Could not resume the campaign.');
    } finally {
      setResuming(false);
    }
  }, [kernel, campaignId, pendingKey, resuming]);

  if (!pendingKey) return null;
  const count = pendingKey.split(',').length;
  const label = resuming
    ? (lang === 'fr' ? 'Reprise…' : 'Resuming…')
    : (lang === 'fr' ? `Reprendre la campagne · ${count} marques` : `Resume campaign · ${count} brand${count === 1 ? '' : 's'}`);

  // The Slides quick-action pill, above the composer like Slides' own (owner, 2026-09-26).
  return (
    <div className="mb-2 flex min-w-0 items-center justify-start gap-1">
      <button
        type="button"
        onClick={resume}
        disabled={resuming}
        className={PILL_CLASS}
        style={PILL_STYLE}
        title={lang === 'fr' ? 'Analyser les marques restées en attente' : 'Analyse the brands this run never reached'}
      >
        {label}
      </button>
    </div>
  );
};

SourceResumeChip.displayName = 'SourceResumeChip';
